<script setup>
import { ref, onMounted, watch, onUnmounted, defineProps, computed } from "vue";
import {
  PencilSquareIcon,
  TrashIcon,
  CheckCircleIcon,
} from "@heroicons/vue/24/solid";
import { getChannels, deleteChannel } from "@/services/channels";

import CreateChannelForm from "./CreateChannelForm.vue";
import { Toast } from "bootstrap";
import ModalDelete from "../ModalDelete.vue";

const isDropdownOpen = ref(false);
const showEditChannelForm = ref(false);
const offcanvasEditChannelFormRef = ref(null);
const props = defineProps({
  data: {
    type: Array,
    required: true,
  },
});
console.log(props.data);

const selectedChannel = ref(null);
const channels = ref([]);
const showSuccessMessage = ref(false);
const isChannelEdited = ref(false);
const deletedChannelName = ref("");

const showDeleteModal = ref(false);
const channelToDelete = ref(null);
const deleteToast = ref(null);

const channelsPerPage = 10;
const currentPage = ref(1);
const totalPages = computed(() => Math.ceil(channels.value.length / channelsPerPage) || 1);
const paginatedChannels = computed(() => {
  const start = (currentPage.value - 1) * channelsPerPage;
  return channels.value.slice(start, start + channelsPerPage);
});

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

onMounted(async () => {
  const clientId = localStorage.getItem("cx");
  const response = await getChannels(clientId);
  console.log("Fetched channels:", response);
  if (response.data.length) {
    channels.value = response.data;
  }
});

onMounted(() => {
  deleteToast.value.addEventListener("shown.bs.toast", () => {
    setTimeout(() => {
      deleteToast.value.classList.remove("show");
    }, 3000);
  });
});

const promptDeleteChannel = (channel) => {
  channelToDelete.value = channel;
  showDeleteModal.value = true;
};

const confirmDeleteChannel = async () => {
  if (!channelToDelete.value) return;
  try {
    await deleteChannel(channelToDelete.value.channelsId);
    deletedChannelName.value = channelToDelete.value.name;
    channels.value = channels.value.filter(
      (c) => c.channelsId !== channelToDelete.value.channelsId
    );

    const bsToast = new Toast(deleteToast.value);
    bsToast.show();
  } catch (error) {
    console.error("Error al eliminar canal:", error);
  } finally {
    showDeleteModal.value = false;
    channelToDelete.value = null;
  }
};

const cancelDeleteChannel = () => {
  showDeleteModal.value = false;
  channelToDelete.value = null;
};

function toggleEditChannelForm(channel) {
  selectedChannel.value = channel;
  console.log("Selected Channel:", selectedChannel.value);
  showEditChannelForm.value = true;
  showSuccessMessage.value = false;
}
/* Click fuera del dropdown */
onMounted(() => {
  document.addEventListener("click", (event) => {
    if (!event.target.closest(".dropdown")) {
      isDropdownOpen.value = false;
    }
  });
});

const handleKeySaved = (newData) => {
  console.log("Datos recibidos para actualizar:", newData);
  // Actualizar la regla en la lista
  const index = channels.value.findIndex(
    (channel) => channel.channelsId === newData.channelsId
  );
  if (index !== -1) {
    channels.value.splice(index, 1, newData);
  } else {
    channels.value.push(newData);
  }
  showEditChannelForm.value = false;
  isChannelEdited.value = true;
  showSuccessMessage.value = true;
};

const closeOffcanvas = () => {
  showEditChannelForm.value = false;
  if (!isChannelEdited.value) {
    showSuccessMessage.value = false;
  }
};

const handleEscape = (event) => {
  if (event.key === "Escape") {
    closeOffcanvas();
  }
};
onMounted(() => {
  window.addEventListener("keydown", handleEscape);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleEscape);
});

// Watcher para detectar cambios en showEditChannelForm
watch(showEditChannelForm, (newValue) => {
  if (!newValue) {
    // Si showEditRuleForm pasa a false (el formulario se cerró),
    // mostrar el mensaje y opciones
    setTimeout(() => {
      const offcanvasElement = document.querySelector(
        "#offcanvasEditChannelForm"
      );
      offcanvasElement.classList.add("show"); // Mostrar el offcanvas
    }, 100);
  }
});
</script>
<template>
  <div>
    <div class="rounded-lg table-container" style="width: 100%">
      <table class="table" style="width: 100%">
        <thead>
          <tr>
            <th>Nombre del Canal</th>
            <th>Descripción</th>
            <th>Proyecto Asignado</th>
            <th class="actions-column"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="channel in paginatedChannels" :key="channel.channelsId">
            <td>{{ channel.name }}</td>
            <td>{{ channel.description }}</td>
            <td>{{ channel.project ? channel.project.name : "" }}</td>
            <td
              class="d-flex gap-3 justify-content-around actions-column mx-2"
              style="margin-top: 3px; border-bottom: none"
            >
              <button
                class="buttons-group"
                data-bs-toggle="offcanvas"
                data-bs-target="#offcanvasEditChannelForm"
                aria-controls="offcanvasEditChannelForm"
                @click="toggleEditChannelForm(channel)"
              >
                <PencilSquareIcon
                  class="text-blue-500 d-flex justify-content-center"
                  style="color: #517fa4; width: 23px; height: 23px"
                ></PencilSquareIcon>
              </button>
              <button
                class="buttons-group delete"
                @click="promptDeleteChannel(channel)"
              >
                <TrashIcon
                  class="text-blue-500 d-flex justify-content-center"
                  style="width: 23px; height: 23px"
                ></TrashIcon>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <!-- PAGINACIÓN -->
      <div class="pagination-container d-flex flex-column align-items-end mt-3">
        <div class="pagination-controls d-flex align-items-center gap-2 justify-content-end">
          <button class="pagination-btn" :disabled="currentPage === 1" @click="prevPage">&lt;</button>
          <button
            v-for="page in totalPages"
            :key="page"
            class="pagination-btn"
            :class="{ active: page === currentPage }"
            @click="goToPage(page)"
          >
            {{ page }}
          </button>
          <button class="pagination-btn" :disabled="currentPage === totalPages" @click="nextPage">&gt;</button>
        </div>
        <div class="pagination-info mt-2 text-end">
          Mostrando
          <b>{{ (currentPage - 1) * channelsPerPage + 1 }}</b>
          -
          <b>{{ Math.min(currentPage * channelsPerPage, channels.length) }}</b>
          de
          <b>{{ channels.length }}</b>
          resultados
        </div>
      </div>
      <!-- FIN PAGINACIÓN -->
      <div
        class="toast align-items-center position-fixed top-50 end-0 p-2 w-auto"
        style="background: #243949; color: white; width: auto"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        id="deleteToast"
        ref="deleteToast"
      >
        <div class="d-flex">
          <div class="toast-body">
            Se ha eliminado el canal
            <span
              style="
                font-weight: 600;
                text-decoration: underline;
                text-transform: capitalize;
              "
            >
              {{ deletedChannelName }}
            </span>
            con éxito.
          </div>
          <button
            type="button"
            class="btn-close me-2 m-auto"
            data-bs-dismiss="toast"
            aria-label="Close"
          ></button>
        </div>
      </div>
    </div>
    <div
      class="offcanvas offcanvas-end"
      style="width: 45%"
      data-bs-scroll="true"
      tabindex="-1"
      id="offcanvasEditChannelForm"
      aria-labelledby="offcanvasEditChannelFormLabel"
      ref="offcanvasEditChannelFormRef"
    >
      <div class="offcanvas-header">
        <button
          type="button"
          class="btn-close"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        ></button>
      </div>
      <div class="offcanvas-body p-4" v-if="showEditChannelForm">
        <CreateChannelForm
          :offcanvasRef="offcanvasEditChannelFormRef"
          :channelDataProp="selectedChannel"
          @updateChannel="handleKeySaved"
          @cancelEdit="closeOffcanvas"
        ></CreateChannelForm>
      </div>
      <!-- *Mensaje -->
      <div
        v-if="showSuccessMessage"
        class="offcanvas-body"
        style="text-align: center; margin-top: 8rem"
      >
        <CheckCircleIcon
          style="width: 5rem; height: 5rem; color: #517fa4"
        ></CheckCircleIcon>
        <h4 class="create-title mt-3">El Canal se editó con éxito.</h4>
        <button
          class="globals-border-button"
          data-bs-dismiss="offcanvas"
          style="margin-top: 3rem"
        >
          Salir
        </button>
      </div>
    </div>
  </div>
  <ModalDelete
    :show="showDeleteModal"
    title="Confirmar Eliminación"
    :message="`¿Estás seguro de que deseas eliminar el canal <strong>${channelToDelete?.name}</strong>?`"
    @confirm="confirmDeleteChannel"
    @cancel="cancelDeleteChannel"
  />
</template>
<style lang="scss" scoped>
@import "@/_scss/global/_variables.scss";
.actions-column {
  position: sticky;
  right: 0;
  margin-left: 4px;
  margin-right: 4px;
}
.buttons-group {
  border: none;
  background: transparent;
  cursor: pointer;
}
.delete:hover {
  background-color: $gradient-color-01;
}
.rounded-lg {
  border-radius: 10px;
  overflow: hidden;
}
.table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  padding: 6px 15px 6px 15px;
  margin-top: 3px;
  margin: 4px;
  text-align: start;
  border-bottom: 1px solid $fourth-color;
  white-space: nowrap;
}

th {
  background-color: $fourth-color;
  color: $primary-color;
  text-align: start;
  padding-top: 3px;
  padding-bottom: 4px;
  margin: 3px 2px 3px 2px;
}

.under-th {
  height: 10px;
  font-size: x-small;
  padding: 2px;
}

.table-container {
  width: 100%;
  max-width: none;
  overflow-x: auto;
  overflow-y: hidden;
}

.table-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
  border-radius: 10px;
  background-color: $white-color;
}

.table-container::-webkit-scrollbar-thumb {
  background-color: $seven-color;
  border-radius: 10px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background-color: $six-color;
}

// Pagination styles
.pagination-container {
  width: 100%;
  margin-top: 1.5rem;
  align-items: flex-end;
}
.pagination-controls {
  gap: 0.5rem;
}
.pagination-btn {
  border: 1.5px solid #355E77;
  background: #fff;
  color: #355E77;
  border-radius: 50%;
  width: 2.2rem;
  height: 2.2rem;
  font-weight: 700;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s, color 0.18s, border 0.18s;
  cursor: pointer;
  margin: 0 2px;
}
.pagination-btn.active, .pagination-btn:hover {
  background: #355E77;
  color: #fff;
  border: 1.5px solid #355E77;
}
.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.pagination-info {
  font-size: 0.98rem;
  color: #355E77;
  margin-top: 0.5rem;
}
</style>
